/* global React, Icon, Card, Button, Modal, KPI, toast, fmt */
// ============================================================
// PLUMME 3 — Privat økonomi · Eiendeler (formue)
// Hus, bil, hytte, sparing, fond m.m. Eiendelene lagres via
// backend (upsert/deletePersonalFinanceAsset). Nøkkeltall og
// likviditet kommer fra readmodel-summary.
//
// Merk: designet viser markedskommentarer hentet fra eksterne
// markedsdata. Plumme har ingen slik kilde ennå, så utvikling
// og prognose regnes utelukkende fra brukerens egen forventede
// verdiendring — vi presenterer ikke påstander vi ikke har data for.
// ============================================================
const { useState: eiUseState, useMemo: eiUseMemo } = React;

const EI_COLORS = ['var(--plum-700)', 'var(--plum-600)', 'var(--plum-500)', 'var(--plum-400)', 'var(--success)', 'var(--warning)', 'var(--plum-300)', 'var(--muted-2)'];
const EI_ASSET_TYPES = [
  'cash', 'savings', 'fund', 'shares',
  'other-investment', 'property', 'vehicle', 'collectible', 'other',
];
const EI_ICONS = [['home', 'Eiendom'], ['car', 'Kjøretøy'], ['building', 'Fritidseiendom'], ['wallet', 'Sparing'], ['trendUp', 'Fond / aksjer'], ['sparkle', 'Verdigjenstander']];
const eiPct = (value) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${Math.abs(Number(value || 0)).toLocaleString(PlummeI18n.intlLocale(), { maximumFractionDigits: 1 })} %`;
const eiAvatar = () => (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';
// Forventet endring per måned, avledet av eiendelens egen årlige verdiendring.
const eiMonthDelta = (asset) => Math.round(Number(asset.value || 0) * Number(asset.growth || 0) / 100 / 12);

// Helsevurdering per eiendel — utledet av forventet årlig verdiendring.
function eiHealth(asset) {
  const growth = Number(asset.growth || 0);
  if (growth <= -8) return { key: 'risiko', label: t('pkn.eie.health.riskLabel'), color: 'var(--error)', bg: 'var(--error-bg)', icon: 'alertCircle', note: t('pkn.eie.health.riskNote') };
  if (growth < 1) return { key: 'press', label: t('pkn.eie.health.pressLabel'), color: 'var(--warning)', bg: 'var(--warning-bg)', icon: 'trendDown', note: t('pkn.eie.health.pressNote') };
  if (growth >= 6) return { key: 'sterk', label: t('pkn.eie.health.strongLabel'), color: 'var(--success)', bg: 'var(--success-bg)', icon: 'trendUp', note: t('pkn.eie.health.strongNote') };
  return { key: 'stabil', label: t('pkn.eie.health.stableLabel'), color: 'var(--plum-700)', bg: 'var(--plum-100)', icon: 'checkCircle', note: t('pkn.eie.health.stableNote') };
}

// Formuehelse — diversifisering, likviditet, risiko og gjeldsgrad.
const eiGrade = (score) => (score >= 85 ? 'A' : score >= 75 ? 'A−' : score >= 65 ? 'B+' : score >= 55 ? 'B' : score >= 45 ? 'C+' : score >= 35 ? 'C' : 'D');
function eiWealthHealth(assets, liquid, total, debt) {
  if (!total) return { score: 0, grade: '—', factors: [] };
  const cats = {};
  assets.forEach((asset) => { cats[asset.category] = (cats[asset.category] || 0) + asset.value; });
  const herfindahl = Object.values(cats).reduce((sum, value) => sum + (value / total) * (value / total), 0);
  const clamp = (value) => Math.max(0, Math.min(1, value));
  const risky = assets.filter((asset) => asset.growth <= -8).reduce((sum, asset) => sum + asset.value, 0);
  const factors = [
    { name: t('pkn.eie.factor.diversification'), pct: clamp((1 - herfindahl) / 0.6), note: t('pkn.eie.factor.diversificationNote') },
    { name: t('pkn.eie.factor.liquidity'), pct: clamp((liquid / total) / 0.25), note: t('pkn.eie.factor.liquidityNote') },
    { name: t('pkn.eie.factor.risk'), pct: clamp(1 - (risky / total) / 0.25), note: t('pkn.eie.factor.riskNote') },
    { name: t('pkn.eie.factor.leverage'), pct: clamp(1 - (debt / total) * 0.6), note: t('pkn.eie.factor.leverageNote') },
  ];
  const score = Math.round(factors.reduce((sum, factor) => sum + factor.pct, 0) / factors.length * 100);
  return { score, grade: eiGrade(score), factors };
}

function EiRing({ score }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const fraction = Math.max(0, Math.min(1, score / 100));
  const color = score >= 75 ? 'var(--success)' : score >= 55 ? 'var(--plum-600)' : score >= 40 ? 'var(--warning)' : 'var(--error)';
  return <svg width="140" height="140" viewBox="0 0 140 140" className="ei-ring">
    <circle cx="70" cy="70" r={radius} fill="none" stroke="var(--panel-2)" strokeWidth="12" />
    <circle cx="70" cy="70" r={radius} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${circumference * fraction} ${circumference}`} transform="rotate(-90 70 70)" style={{ transition: 'stroke-dasharray .5s var(--ease,ease)' }} />
    <text x="70" y="66" textAnchor="middle" fontSize="34" fontWeight="900" fill="var(--ink)">{score}</text>
    <text x="70" y="88" textAnchor="middle" fontSize="12" fontWeight="800" fill="var(--muted)">{t('po.outOf100')}</text>
  </svg>;
}

// «Forklar» — bryter ned hva den forventede verdiendringen betyr i kroner.
function EiExplainModal({ asset, onClose }) {
  const health = eiHealth(asset);
  const monthDelta = eiMonthDelta(asset);
  const in6 = Math.round(asset.value * (1 + asset.growth / 100 * 0.5));
  const in12 = Math.round(asset.value * (1 + asset.growth / 100));
  return <Modal open onClose={onClose} max={580}
    title={<span className="ei-explain-title"><span className="ei-explain-tico" style={{ background: asset.color }}><Icon name={asset.icon} size={17} /></span>{asset.name}</span>}
    footer={<Button onClick={onClose}>{t('common.close')}</Button>}>
    <div className="ei-explain">
      <div className="ei-explain-top">
        <span className="ei-health" style={{ color: health.color, background: health.bg }}><Icon name={health.icon} size={13} />{health.label}</span>
        <span className="ei-explain-val">{fmt(asset.value)}<small style={{ color: monthDelta > 0 ? 'var(--success)' : monthDelta < 0 ? 'var(--error)' : 'var(--muted)' }}> {`${monthDelta > 0 ? '+' : monthDelta < 0 ? '−' : ''}${fmt(Math.abs(monthDelta))}`} {t('pkn.eie.expectedPerMonth')}</small></span>
      </div>
      <div className="ei-explain-block">
        <div className="ei-explain-h"><span className="ei-explain-ava"><img src={eiAvatar()} alt="SuperPlumma" /></span>{t('po.whatFiguresMean')}</div>
        <p>{t('sharedText.po.youHaveStated')} <b>{asset.name}</b> {t('pkn.eie.expectedToChange')} <b>{eiPct(asset.growth)}</b> {t('pkn.eie.perYearWithValue', { value: fmt(asset.value), amount: fmt(Math.abs(monthDelta)), kind: monthDelta < 0 ? t('pkn.eie.valueFall') : t('pkn.eie.valueGrowth') })} {health.note}.</p>
      </div>
      <div className="ei-explain-block">
        <div className="ei-explain-h"><Icon name="trendUp" size={15} style={{ color: 'var(--plum-700)' }} />{t('po.howItLooksAhead')}</div>
        <p>{t('pkn.eie.projectionNote')}</p>
        <div className="ei-explain-proj">
          <div className="ei-proj"><span>{t('po.inSixMonths')}</span><b style={{ color: in6 >= asset.value ? 'var(--success)' : 'var(--error)' }}>{fmt(in6)}</b></div>
          <div className="ei-proj"><span>{t('po.inTwelveMonths')}</span><b style={{ color: in12 >= asset.value ? 'var(--success)' : 'var(--error)' }}>{fmt(in12)}</b></div>
        </div>
      </div>
    </div>
  </Modal>;
}

function EiAssetForm({ asset, onClose, onDelete, onSave }) {
  const [draft, setDraft] = eiUseState(() => asset || { assetType: 'other', category: '', color: EI_COLORS[0], detail: '', growth: '', icon: 'home', name: '', taxValue: '', value: '' });
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const field = (label, key, opts = {}) => <label className="gj-field">
    <span>{label}</span>
    <input className="gj-input" type={opts.type || 'text'} value={draft[key] ?? ''} placeholder={opts.placeholder || ''} onChange={(event) => set(key, event.target.value)} />
  </label>;
  const save = () => {
    if (!String(draft.name || '').trim() || draft.value === '' || draft.value === null) {
      toast(t('pkn.eie.fillNameValue'), 'error');
      return;
    }
    onSave({
      ...draft,
      assetType: draft.assetType || 'other',
      category: String(draft.category || '').trim() || String(draft.name || '').trim(),
      color: draft.color || EI_COLORS[0],
      detail: String(draft.detail || '').trim(),
      growth: Number(draft.growth || 0),
      icon: draft.icon || 'home',
      id: draft.id || `asset-${Date.now()}`,
      name: String(draft.name || '').trim(),
      taxValue: draft.taxValue === '' || draft.taxValue === null || draft.taxValue === undefined ? null : Number(draft.taxValue || 0),
      value: Number(draft.value || 0),
    });
  };
  return <Modal open onClose={onClose} title={asset ? t('pkn.eie.editAsset') : t('pkn.eie.addAsset')} max={560}
    footer={<React.Fragment>
      {asset && onDelete && <Button variant="ghost" icon="trash" onClick={() => onDelete(asset)}>{t('list.delete')}</Button>}
      <div style={{ flex: 1 }} />
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="check" onClick={save}>{t('sale.save')}</Button>
    </React.Fragment>}>
    <div className="gj-form">
      {field(t('pkn.eie.fieldName'), 'name', { placeholder: t('pkn.eie.phName') })}
      {field(t('pkn.eie.fieldDetail'), 'detail', { placeholder: t('pkn.eie.phDetail') })}
      <div className="gj-form-row">
        {field(t('pkn.eie.fieldValue'), 'value', { type: 'number', placeholder: '0' })}
        {field(t('pkn.eie.fieldTaxValue'), 'taxValue', { type: 'number', placeholder: '0' })}
      </div>
      <div className="gj-form-row">
        {field(t('pkn.eie.fieldGrowth'), 'growth', { type: 'number', placeholder: t('pkn.eie.phGrowth') })}
      </div>
      <label className="gj-field"><span>{t('pkn.eie.assetType')}</span>
        <select className="gj-input" value={draft.assetType || 'other'} onChange={(event) => set('assetType', event.target.value)}>
          {EI_ASSET_TYPES.map((value) => <option key={value} value={value}>{t('pkn.eie.type.' + value)}</option>)}
        </select>
      </label>
      <label className="gj-field"><span>{t('cust.ledger.type')}</span>
        <div className="gj-icon-pick">
          {EI_ICONS.map(([icon, label]) => <button key={icon} type="button" className={`gj-icon-opt${draft.icon === icon ? ' on' : ''}`} title={t('pkn.eie.icon.' + icon)}
            onClick={() => { set('icon', icon); if (!draft.category) set('category', label); }}><Icon name={icon} size={18} /></button>)}
        </div>
      </label>
      <label className="gj-field"><span>{t('buy.category')}</span>
        <input className="gj-input" value={draft.category} placeholder={t('po.assetCategoryPh')} onChange={(event) => set('category', event.target.value)} />
        <div className="gj-color-pick">
          {EI_COLORS.map((color) => <button key={color} type="button" className={`gj-swatch${draft.color === color ? ' on' : ''}`} style={{ background: color }} onClick={() => set('color', color)} aria-label={t('pkn.eie.pickColor', { color })} />)}
        </div>
      </label>
    </div>
  </Modal>;
}

function EiCard({ asset, onClick }) {
  const monthDelta = eiMonthDelta(asset);
  const health = eiHealth(asset);
  const readOnly = asset.isReadOnly || asset.source === 'account';
  return <button className="gj-card" onClick={onClick}>
    <div className="gj-card-top">
      <span className="gj-card-ico" style={{ background: asset.color }}><Icon name={asset.icon} size={19} /></span>
      <div className="gj-card-head">
        <div className="gj-card-name">{asset.name}</div>
        <div className="gj-card-type">{asset.detail || asset.category}</div>
      </div>
      {readOnly
        ? <Icon name="lock" size={15} style={{ color: 'var(--muted-2)', flex: 'none' }} />
        : <Icon name="pencil" size={15} style={{ color: 'var(--muted-2)', flex: 'none' }} />}
    </div>
    <div className="gj-card-bal">{fmt(asset.value)}</div>
    {asset.taxValue !== null && asset.taxValue !== undefined && <div className="gj-card-market"><Icon name="percent" size={12} />{t('pkn.eie.taxBasis', { amount: fmt(asset.taxValue) })}</div>}
    <div className="ei-since" style={{ color: monthDelta > 0 ? 'var(--success)' : monthDelta < 0 ? 'var(--error)' : 'var(--muted)' }}>
      {monthDelta !== 0 && <Icon name={monthDelta > 0 ? 'trendUp' : 'trendDown'} size={12} />}
      {`${monthDelta > 0 ? '+' : monthDelta < 0 ? '−' : ''}${fmt(Math.abs(monthDelta))}`} {t('pkn.eie.expectedPerMonth')}
    </div>
    <div className="gj-card-foot" style={{ marginTop: 10 }}>
      <span className="ei-health" style={{ color: health.color, background: health.bg }}><Icon name={health.icon} size={12} />{health.label}</span>
      <span className="ei-change" style={{ color: asset.growth > 0 ? 'var(--success)' : asset.growth < 0 ? 'var(--error)' : 'var(--muted)' }}>
        {asset.growth !== 0 && <Icon name={asset.growth > 0 ? 'trendUp' : 'trendDown'} size={13} />}{eiPct(asset.growth)}{t('pkn.eie.perYearSuffix')}
      </span>
    </div>
  </button>;
}

function PoEiendeler({ assets = [], cashTotal = null, debtTotal = 0, liquidTotal = null, marketableInvestmentsTotal = null, netWorth = null, onDeleteAsset, onSaveAsset }) {
  const Sec = window.PoSecHead;
  const [formAsset, setFormAsset] = eiUseState(null);
  const [explain, setExplain] = eiUseState(null);

  const total = assets.reduce((sum, asset) => sum + asset.value, 0);
  const yearChange = assets.reduce((sum, asset) => sum + asset.value * asset.growth / 100, 0);
  const assetLiquid = assets.filter((asset) => /sparing|fond|aksj|konto|likvid/i.test(asset.category)).reduce((sum, asset) => sum + asset.value, 0);
  const liquid = liquidTotal === null || liquidTotal === undefined ? assetLiquid : Number(liquidTotal || 0);
  const debt = Number(debtTotal || 0);
  const net = netWorth === null || netWorth === undefined ? total - debt : Number(netWorth || 0);

  const byCategory = eiUseMemo(() => {
    const map = new Map();
    assets.forEach((asset) => {
      const entry = map.get(asset.category) || { category: asset.category, color: asset.color, sum: 0 };
      entry.sum += asset.value;
      map.set(asset.category, entry);
    });
    return [...map.values()].sort((a, b) => b.sum - a.sum);
  }, [assets]);

  const wealth = assets.length ? eiWealthHealth(assets, liquid, total, debt) : null;
  const weakest = wealth && wealth.factors.length ? [...wealth.factors].sort((a, b) => a.pct - b.pct)[0] : null;
  const strongest = wealth && wealth.factors.length ? [...wealth.factors].sort((a, b) => b.pct - a.pct)[0] : null;
  const verdict = wealth ? (wealth.score >= 75 ? t('pkn.eie.verdict.solid') : wealth.score >= 55 ? t('pkn.eie.verdict.healthy') : t('pkn.eie.verdict.rebalance')) : '';
  const monthTotal = assets.reduce((sum, asset) => sum + eiMonthDelta(asset), 0);
  const watch = assets.map((asset) => ({ asset, health: eiHealth(asset) }))
    .filter((entry) => entry.health.key === 'risiko' || entry.health.key === 'press')
    .sort((a, b) => a.asset.growth - b.asset.growth);

  return <React.Fragment>
    <div>
      <Sec icon="wallet" title={t('po.assetsAndWealth')} sub={t('po.assetsAndWealthSub')} />
      <div className="grid-3" style={{ gap: 14, marginTop: 14 }} data-help-title={t('po.assetsAndWealth')} data-help={t('po.assetsHelp')}>
        <KPI tint="plum" icon="home" label={t('po.totalAssets')} value={assets.length ? fmt(total) : t('pkn.eie.notRegistered')}  help={t('helpTopics.netWealth.body')}/>
        <KPI tint="peach" icon="card" label={t('clo.debt')} value={debt ? fmt(debt) : t('pkn.eie.notRegistered')} />
        <KPI tint="green" icon="trendUp" label={t('po.netWealth')} value={assets.length || debt ? fmt(net) : t('pkn.eie.notRegistered')}  help={t('helpTopics.netWealth.body')}/>
        <KPI tint="white" icon="percent" label={t('po.expectedChangePerYear')} value={assets.length ? `${yearChange >= 0 ? '+' : '−'}${fmt(Math.abs(Math.round(yearChange)))}` : t('pkn.eie.notRegistered')}  help={t('helpTopics.assetEstimate.body')}/>
        <KPI tint="white" icon="wallet" label={t('po.liquid')} value={liquid ? fmt(liquid) : t('pkn.eie.notRegistered')} sub={t('pkn.eie.liquidSub', { cash: fmt(cashTotal || 0), investments: fmt(marketableInvestmentsTotal || 0) })}  help={t('helpTopics.assetEstimate.body')}/>
        <KPI tint="plum" icon="list" label={t('po.assetCount')} value={String(assets.length)} />
      </div>
      <div className="t-xs muted" style={{ marginTop: 10 }}>{t('pkn.eie.liquidNote')}</div>
    </div>

    {wealth && <Card>
      <Sec icon="checkCircle" title={t('po.wealthHealth')} sub={t('po.wealthHealthSub')} />
      <div className="ei-wh-grid">
        <div className="ei-wh-ringwrap">
          <EiRing score={wealth.score} />
          <span className="ei-wh-grade" style={{ color: wealth.score >= 75 ? 'var(--success)' : wealth.score >= 55 ? 'var(--plum-700)' : 'var(--warning)' }}>{wealth.grade}</span>
          <span className="ei-wh-verdict">{verdict}</span>
        </div>
        <div className="ei-wh-factors">
          {wealth.factors.map((factor) => <div key={factor.name} className="ei-wh-factor">
            <div className="ei-wh-ftop"><span className="ei-wh-fname">{factor.name}</span><span className="ei-wh-fval">{Math.round(factor.pct * 100)}</span></div>
            <div className="ei-wh-ftrack"><div style={{ width: `${factor.pct * 100}%`, background: factor.pct >= 0.7 ? 'var(--success)' : factor.pct >= 0.45 ? 'var(--warning)' : 'var(--error)' }} /></div>
            <div className="ei-wh-fnote">{factor.note}</div>
          </div>)}
        </div>
      </div>
      {strongest && weakest && <div className="ei-mkt-scan" style={{ marginTop: 16 }}>
        <span className="ei-mkt-ava"><img src={eiAvatar()} alt="SuperPlumma" /></span>
        <p>{t('sharedText.po.wealthStrongestOn')} <b>{strongest.name.toLowerCase()}</b>. {t('pkn.eie.watchMostPrefix')} <b>{weakest.name.toLowerCase()}</b> — {weakest.pct < 0.5 ? t('pkn.eie.mostRoom') : t('pkn.eie.fineButBetter')}</p>
      </div>}
    </Card>}

    {assets.length > 0 && <Card>
      <Sec icon="pie" title={t('home.ana.ring')} sub={t('po.wealthDistribution')} />
      <div className="ei-alloc-bar" style={{ marginTop: 14 }}>
        {byCategory.map((entry) => <div key={entry.category} className="ei-alloc-seg" style={{ width: `${entry.sum / total * 100}%`, background: entry.color }} title={entry.category} />)}
      </div>
      <div className="ei-legend">
        {byCategory.map((entry) => <div key={entry.category} className="ei-legend-item">
          <span className="ei-legend-dot" style={{ background: entry.color }} />
          <span className="ei-legend-name">{entry.category}</span>
          <span className="ei-legend-val">{fmt(entry.sum)}</span>
          <span className="ei-legend-pct">{Math.round(entry.sum / total * 100)} %</span>
        </div>)}
      </div>
    </Card>}

    {assets.length > 0 && <Card>
      <Sec icon="sparkle" title={t('po.expectedDevelopment')} sub={t('po.expectedDevelopmentSub')}
        right={<span className="ei-mkt-total" style={{ color: monthTotal > 0 ? 'var(--success)' : monthTotal < 0 ? 'var(--error)' : 'var(--muted)', background: monthTotal > 0 ? 'var(--success-bg)' : monthTotal < 0 ? 'var(--error-bg)' : 'var(--panel-2)' }}>{`${monthTotal >= 0 ? '+' : '−'}${fmt(Math.abs(monthTotal))}`} {t('pkn.eie.perMonth')}</span>}
        data-help-title={t('po.expectedDevelopment')} data-help={t('po.projectionHelp')} />
      <div className="ei-mkt-scan">
        <span className="ei-mkt-ava"><img src={eiAvatar()} alt="SuperPlumma" /></span>
        <p>{t('sharedText.po.withYourAssumptions')} <b style={{ color: monthTotal >= 0 ? 'var(--success)' : 'var(--error)' }}>{`${monthTotal >= 0 ? '+' : '−'}${fmt(Math.abs(monthTotal))}`}</b> {t('pkn.eie.monthlyForecastNote')}</p>
      </div>
      <div className="ei-mkt-list">
        {assets.map((asset) => {
          const monthDelta = eiMonthDelta(asset);
          const health = eiHealth(asset);
          return <div key={asset.id} className="ei-mkt-row">
            <span className="ei-mkt-ico" style={{ background: asset.color }}><Icon name={asset.icon} size={18} /></span>
            <div className="ei-mkt-body">
              <div className="ei-mkt-head">
                <span className="ei-mkt-name">{asset.name}<span className="ei-health" style={{ color: health.color, background: health.bg, marginLeft: 9 }}><Icon name={health.icon} size={11} />{health.label}</span></span>
                <span className="ei-mkt-delta" style={{ color: monthDelta > 0 ? 'var(--success)' : monthDelta < 0 ? 'var(--error)' : 'var(--muted)' }}>{`${monthDelta > 0 ? '+' : monthDelta < 0 ? '−' : ''}${fmt(Math.abs(monthDelta))}`} <small>({eiPct(asset.growth)}{t('pkn.eie.perYearSuffix')})</small></span>
              </div>
              <div className="ei-mkt-driver">{health.note}. {t('pkn.eie.valueToday', { value: fmt(asset.value) })}</div>
              <button className="ei-explain-btn" onClick={() => setExplain(asset)}><Icon name="sparkle" size={14} />{t('po.explainSimply')}<Icon name="chevRight" size={14} /></button>
            </div>
          </div>;
        })}
      </div>
    </Card>}

    {assets.length > 0 && <Card>
      <Sec icon="alertCircle" title={t('po.watchingClosely')} sub={t('po.watchingCloselySub')} />
      {watch.length === 0
        ? <div className="ei-watch-clear"><Icon name="checkCircle" size={20} /><p>{t('pkn.eie.watchClear')}</p></div>
        : <div className="ei-watch-list">
          {watch.map(({ asset, health }) => <div key={asset.id} className="ei-watch-row" style={{ '--wc': health.color }}>
            <span className="ei-watch-ico" style={{ color: health.color, background: health.bg }}><Icon name={health.icon} size={18} /></span>
            <div className="ei-watch-body">
              <div className="ei-watch-head"><span className="ei-watch-name">{asset.name}</span><span className="ei-health" style={{ color: health.color, background: health.bg }}>{health.label}</span></div>
              <p className="ei-watch-why">{t(health.key === 'risiko' ? 'pkn.eie.fallWarn' : 'pkn.eie.slowWarn', { pct: eiPct(asset.growth) })} {t('pkn.eie.monthlyEquiv', { amount: fmt(Math.abs(eiMonthDelta(asset))) })}</p>
            </div>
            <button className="ei-watch-cta" onClick={() => setExplain(asset)}>{t('po.explain')}<Icon name="arrowRight" size={14} /></button>
          </div>)}
        </div>}
    </Card>}

    <Card>
      <Sec icon="home" title={t('po.yourAssets')} sub={t('po.yourAssetsSub')}
        right={<Button size="sm" icon="plus" onClick={() => setFormAsset({})}>{t('po.addAsset')}</Button>} />
      {assets.length === 0
        ? <div className="gj-empty">{t('pkn.eie.empty')}</div>
        : <div className="gj-list" style={{ marginTop: 14 }}>
          {assets.map((asset) => <EiCard key={asset.id} asset={asset} onClick={() => (asset.isReadOnly || asset.source === 'account' ? setExplain(asset) : setFormAsset(asset))} />)}
        </div>}
    </Card>

    {formAsset && <EiAssetForm
      asset={formAsset.id ? formAsset : null}
      onClose={() => setFormAsset(null)}
      onDelete={(asset) => { onDeleteAsset?.(asset); setFormAsset(null); }}
      onSave={(asset) => { onSaveAsset?.(asset); setFormAsset(null); }}
    />}
    {explain && <EiExplainModal asset={explain} onClose={() => setExplain(null)} />}
  </React.Fragment>;
}

Object.assign(window, { PoEiendeler });
